import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { PlaceCard } from '../components/ui/card-22';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

const blogs = [
  {
    id: 1,
    title: 'Why Your Business Needs a Professional Website in 2025',
    description: "If you're running a business without a website, you're invisible to millions of potential customers searching online right now. In 2025, a professional website is no longer a luxury. It's the single most important digital asset your business can own.",
    dateRange: 'April 20, 2026',
    hostType: 'Business Growth',
    tags: ['Web Design', 'Digital Presence'],
    rating: 5.0,
    isTopRated: true,
    readTime: '4 min',
    images: [
      '/blogs/blog-1-new.png',
      '/blogs/blog-3.png'
    ],
    link: '/blog/why-your-business-needs-a-website',
  },
  {
    id: 2,
    title: 'Why Business Automation Is the Smartest Investment You Can Make in 2025',
    description: 'Every hour your team spends on repetitive manual tasks is an hour not spent on strategy, sales, or growth. Business automation, powered by AI, is rapidly changing the way UK businesses operate, scale, and compete.',
    dateRange: 'April 15, 2026',
    hostType: 'Automation',
    tags: ['AI', 'Workflows'],
    rating: 4.9,
    isTopRated: true,
    readTime: '5 min',
    images: [
      '/blogs/blog-2-v2.png',
      '/blogs/blog-4.png'
    ],
    link: '/blog/why-automation-is-better-for-business',
  },
  {
    id: 3,
    title: 'Custom Apps That Help Your Business Grow: The EsyAI Advantage',
    description: "Off-the-shelf software is designed for everyone, which means it's perfectly tailored for no one. Custom apps, built specifically for your business, solve your exact problems and unlock growth that one-size-fits-all software never can.",
    dateRange: 'April 10, 2026',
    hostType: 'Custom Apps',
    tags: ['Software', 'Scaling'],
    rating: 4.8,
    isTopRated: false,
    readTime: '6 min',
    images: [
      '/blogs/blog-3-new.png',
      '/blogs/blog-1.png'
    ],
    link: '/blog/custom-apps-that-help-your-business-grow',
  },
  {
    id: 4,
    title: 'AI-Powered Marketing Strategies That Drive Real Business Growth',
    description: 'Marketing has changed more in the last three years than in the previous three decades. Artificial intelligence has fundamentally transformed how businesses reach, engage, and convert customers.',
    dateRange: 'April 05, 2026',
    hostType: 'Marketing',
    tags: ['SEO', 'Marketing'],
    rating: 4.9,
    isTopRated: false,
    readTime: '5 min',
    images: [
      '/blogs/blog-4-new.png',
      '/blogs/blog-2.png'
    ],
    link: '/blog/ai-powered-marketing-strategies-for-business',
  },
  {
    id: 5,
    title: 'What Are AI Agents and How Can They Help Your Business?',
    description: 'Artificial intelligence has moved well beyond chatbots and simple automation. The next frontier is AI agents, and they are already transforming the way forward-thinking businesses operate.',
    dateRange: 'May 01, 2026',
    hostType: 'AI Agents',
    tags: ['Future Tech', 'Automation'],
    rating: 5.0,
    isTopRated: true,
    readTime: '7 min',
    images: [
      '/blogs/blog-5-new.png',
      '/blogs/blog-6.png'
    ],
    link: '/blog/what-are-ai-agents-and-how-they-help-your-business',
  },
  {
    id: 6,
    title: 'How AI Chatbots Transform Your Customer Service',
    description: 'Customer expectations have never been higher. People want instant answers and seamless support. AI chatbots change everything, delivering 24/7 service at scale.',
    dateRange: 'May 05, 2026',
    hostType: 'Customer Service',
    tags: ['Chatbots', 'Support'],
    rating: 4.9,
    isTopRated: false,
    readTime: '5 min',
    images: [
      '/blogs/blog-6-new.png',
      '/blogs/blog-5.png'
    ],
    link: '/blog/how-ai-chatbots-transform-customer-service',
  },
];

export default function Blogs() {
  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      
      {/* Video Background */}
      <div className="absolute inset-0 z-0 h-[600px] md:h-[700px]">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-50"
        >
          <source src={require('../assets/Blogpage-herobg_compressed.mp4')} type="video/mp4" />
        </video>
        {/* Gradient overlay to smoothly blend the video into the black background below */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/60 to-black z-10" />
      </div>
      
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
        
        {/* Header */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible"
          className="text-center mb-20"
        >
          <div className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-6">
            <span className="text-sm font-medium text-white font-body tracking-wide">Insights & Updates</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading italic tracking-tight leading-[0.9] mb-8">
            Thoughts on building <br className="hidden md:block" /> the future.
          </h1>
          <p className="text-white/60 font-body font-light text-lg max-w-2xl mx-auto leading-relaxed">
            Dive into our latest articles on web development, automation, and how AI is transforming the digital landscape.
          </p>
        </motion.div>

        {/* Blog Grid using PlaceCard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {blogs.map((blog) => (
            <Link key={blog.id} to={blog.link} className="flex h-full w-full max-w-sm mx-auto group">
              <PlaceCard
                images={blog.images}
                tags={blog.tags}
                rating={blog.rating}
                title={blog.title}
                dateRange={blog.dateRange}
                hostType={blog.hostType}
                isTopRated={blog.isTopRated}
                description={blog.description}
                pricePerNight={blog.readTime}
                className="h-full w-full hover:-translate-y-2 transition-all duration-500 ease-out"
              />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
